/**
 * Accelerated festival clock. 1 real hour = 6 simulated hours.
 *
 * The festival is a 4-day loop that repeats forever. Both client and server
 * derive sim time from a single epoch so we never need to send the clock
 * across the wire — just the epoch.
 *
 * See SPEC.md §1 (Time and Schedule).
 */

export const TIME_ACCELERATION = 6;
export const SIM_DAY_MS = 24 * 60 * 60 * 1000;
export const SIM_FESTIVAL_DAYS = 4;
export const SIM_FESTIVAL_MS = SIM_DAY_MS * SIM_FESTIVAL_DAYS;
/** Real-time milliseconds for one full simulated festival loop. */
export const REAL_FESTIVAL_LOOP_MS = SIM_FESTIVAL_MS / TIME_ACCELERATION;

export interface FestivalTime {
  /** Whole days into the festival, 0..3 */
  day: number;
  /** Hour of day, 0..23 */
  hour: number;
  /** Minute of hour, 0..59 */
  minute: number;
  /** Total milliseconds into the festival loop. */
  simMs: number;
  /** Normalized 0..1 progress through the loop. */
  progress: number;
}

/**
 * Sim-time offset added to every clock read so the festival doesn't begin
 * at midnight. 17h = 5pm, golden-hour pasture energy with warm sun and
 * dramatic light shafts.
 */
const SIM_START_HOUR_OFFSET = 17;
const SIM_OFFSET_MS = SIM_START_HOUR_OFFSET * 60 * 60 * 1000;

/** Compute the current sim time from a wall-clock millisecond epoch. */
export function festivalTimeFromEpoch(epochMs: number, nowMs: number = Date.now()): FestivalTime {
  const realOffset = ((nowMs - epochMs) * TIME_ACCELERATION + SIM_OFFSET_MS) % SIM_FESTIVAL_MS;
  const simMs = realOffset;
  const day = Math.floor(simMs / SIM_DAY_MS);
  const remaining = simMs % SIM_DAY_MS;
  const hour = Math.floor(remaining / (60 * 60 * 1000));
  const minute = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
  return { day, hour, minute, simMs, progress: simMs / SIM_FESTIVAL_MS };
}

/** Map sim hour 0..24 to a 0..1 day-night curve where 0 = midnight, 0.5 = noon. */
export function dayNightFactor(time: FestivalTime): number {
  return (time.hour + time.minute / 60) / 24;
}

/** Format as "Day 2 · 18:42" for HUD display. */
export function formatFestivalTime(t: FestivalTime): string {
  const hh = String(t.hour).padStart(2, '0');
  const mm = String(t.minute).padStart(2, '0');
  return `Day ${t.day + 1} · ${hh}:${mm}`;
}
